
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserProfileSettings } from './UserProfileSettings';
import { NotificationSettings } from './NotificationSettings';
import { CloudStorageSettings } from './CloudStorageSettings';
import { DummyDataManager } from './DummyDataManager';
import { TeamManagementModal } from './TeamManagementModal';

// import { SuperAdminAccessGate } from './SuperAdmin/SuperAdminAccessGate';
import { AdvancedSecuritySettings } from './AdvancedSecuritySettings';
import { SubscriptionUpgradeModal } from './SubscriptionUpgradeModal';
import { AccessControlDebugger } from './AccessControlDebugger';
import { UpgradeErrorHandler } from './UpgradeErrorHandler';
import { SampleDataManager } from './SampleDataManager';
// import { useSuperAdminAccess } from '@/hooks/useSuperAdminAccess';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import {
  User,
  Bell,
  Cloud,
  Database,
  Users,
  Settings,
  Shield,
  CreditCard,
  HelpCircle,
  ShieldAlert,
  Building,
  Server,
  Menu
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ErpIntegrationConfig } from './ErpIntegrationConfig';

export const SettingsMain = () => {
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // const { isSuperAdmin, loading } = useSuperAdminAccess();
  const { user } = useAuth();
  const [userPlan, setUserPlan] = useState<'basic' | 'individual' | 'corporate' | 'enterprise'>('basic');
  const [subscriptionData, setSubscriptionData] = useState<any>(null);

  // Read 'tab' query parameter from URL
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tabParam = params.get('tab');
  const upgradePlanParam = params.get('upgradePlan');
  const validTabs = [
    'profile', 'notifications', 'team', 'data', 'erp-integration', 'advanced' //, 'super-admin'
  ];
  // const validTabs = [
  //   'profile', 'notifications', 'team', 'storage', 'data', 'erp-integration', 'advanced', 'super-admin'
  // ];
  const initialTab = validTabs.includes(tabParam || '') ? tabParam : 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [focusedUpgradePlan, setFocusedUpgradePlan] = useState<'Individual' | 'Corporate' | 'Enterprise' | undefined>(undefined);

  const tabs = [
    { value: 'profile', label: 'Profile', icon: User },
    { value: 'notifications', label: 'Notifications', icon: Bell },
    { value: 'team', label: 'Team', icon: Users },
    { value: 'data', label: 'Data', icon: Database },
    { value: 'advanced', label: 'Advanced', icon: Settings },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Profile';

  // Update tab if URL changes
  React.useEffect(() => {
    if (validTabs.includes(tabParam || '')) {
      setActiveTab(tabParam!);
    }
  }, [tabParam]);

  React.useEffect(() => {
    const normalizedPlan = upgradePlanParam?.toLowerCase();
    if (normalizedPlan === 'individual' || normalizedPlan === 'corporate' || normalizedPlan === 'enterprise') {
      const mappedPlan = `${normalizedPlan.charAt(0).toUpperCase()}${normalizedPlan.slice(1)}` as 'Individual' | 'Corporate' | 'Enterprise';
      setFocusedUpgradePlan(mappedPlan);
      setShowUpgradeModal(true);
    } else {
      setFocusedUpgradePlan(undefined);
    }
  }, [upgradePlanParam]);

  useEffect(() => {
    fetchPlan();
  }, [user]);

  // Show super admin tab if user has access OR if they're not yet initialized (to show initializer)
  // const showSuperAdminTab = !loading;

  const handleUpgradeSuccess = () => {
    // Refresh subscription data after upgrade
    if (user?.id) {
      fetchPlan();
    }
  };

  const fetchPlan = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          plan_id,
          status,
          start_date,
          end_date,
          trial_ends_at,
          subscription_plans!user_subscriptions_plan_id_fkey (
            name,
            description,
            price,
            currency,
            billing_interval,
            features
          )
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'trial'])
        .single();

      if (error) {
        console.warn('Could not fetch subscription plan:', error);
        setUserPlan('basic'); // Default to free basic plan
        setSubscriptionData(null);
        return;
      }

      const normalizedPlan = (data?.subscription_plans?.name || 'basic').toLowerCase();
      // Map the plan name to the expected union type
      const mappedPlan = normalizedPlan === 'individual' || normalizedPlan === 'corporate' || normalizedPlan === 'enterprise'
        ? normalizedPlan
        : 'basic';
      setUserPlan(mappedPlan);
      setSubscriptionData(data);
    } catch (error) {
      console.warn('Error fetching subscription plan:', error);
      setUserPlan('basic'); // Default to free basic plan
      setSubscriptionData(null);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-gray-600">Manage your account preferences and system configuration</p>
        </div>
        {/* Removed Help & Support button */}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
        {/* Desktop Tabs List */}
        <TabsList className="hidden md:grid w-full grid-cols-5">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Mobile Tabs Dropdown */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                <Menu className="h-5 w-5 text-gray-500" />
                <span className="flex items-center gap-2 border-l pl-3">
                  {React.createElement(tabs.find(t => t.value === activeTab)?.icon || User, { className: "h-5 w-5" })}
                  <span className="font-semibold">{activeTabLabel}</span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="start">
              {tabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className="flex items-center gap-2 py-3"
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent value="profile">
          <UserProfileSettings />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>

        {/* Team tab content for all users */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Management
              </CardTitle>
              <CardDescription>
                Manage your sustainability team members and collaboration settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">Team Collaboration</h3>
                    <p className="text-sm text-gray-600">Invite team members to collaborate on sustainability data and reports</p>
                  </div>
                  <Button onClick={() => setShowTeamManagement(true)}>Manage Team</Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">Role Permissions</h3>
                    <p className="text-sm text-gray-600">Configure access levels and permissions for different team roles</p>
                  </div>
                  <Button variant="outline">Configure Roles</Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">Audit Logs</h3>
                    <p className="text-sm text-gray-600">View team activity and data modification history</p>
                  </div>
                  <Button variant="outline">View Logs</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="storage">
          <CloudStorageSettings />
        </TabsContent> */}

        <TabsContent value="data">
          <DummyDataManager />
          <SampleDataManager />
        </TabsContent>

        {/* <TabsContent value="erp-integration">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                ERP System Integration
              </CardTitle>
              <CardDescription>
                Configure integrations with your existing ERP systems for seamless data synchronization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ErpIntegrationConfig
                integrationType="sap"
                onConfigChange={(config) => console.log('ERP config changed:', config)}
                onTestConnection={async () => {
                  // Simulate connection test
                  await new Promise(resolve => setTimeout(resolve, 1000));
                  return Math.random() > 0.5;
                }}
              />
            </CardContent>
          </Card>
        </TabsContent> */}

        <TabsContent value="advanced" className="space-y-6">
          {/* Debug components visible to all users */}
          <AccessControlDebugger />
          <UpgradeErrorHandler />

          <AdvancedSecuritySettings />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Billing & Subscription
              </CardTitle>
              <CardDescription>
                Manage your subscription and billing information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      Current Plan
                      {subscriptionData?.status === 'trial' && (
                        <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                          Free Trial
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {subscriptionData?.subscription_plans?.name || 'Basic'} Plan - 
                      {subscriptionData?.status === 'trial'
                        ? 'Trial Mode'
                        : subscriptionData?.subscription_plans?.price
                          ? `$${subscriptionData.subscription_plans.price}/${subscriptionData?.subscription_plans?.billing_interval || 'month'}`
                          : 'Free'}
                    </p>
                    {user?.email && (
                      <p className="text-xs text-gray-500 mt-1">
                        Billing Email: {user.email}
                      </p>
                    )}
                    {subscriptionData?.start_date && (
                      <p className="text-xs text-gray-500">
                        Started: {new Date(subscriptionData.start_date).toLocaleDateString()}
                      </p>
                    )}
                    {subscriptionData?.status === 'trial' && subscriptionData?.trial_ends_at && (
                      <p className="text-xs text-amber-600 font-medium mt-1">
                        Trial ends: {new Date(subscriptionData.trial_ends_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => setShowUpgradeModal(true)}>
                    Upgrade Plan
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">Payment Method</h3>
                    <p className="text-sm text-gray-600">
                      Manage your payment methods and billing history
                    </p>
                  </div>
                  <Button variant="outline">
                    Update Payment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="super-admin">
          <SuperAdminAccessGate />
        </TabsContent> */}
      </Tabs>

      <TeamManagementModal
        isOpen={showTeamManagement}
        onClose={() => setShowTeamManagement(false)}
        userPlan={userPlan}
      />

      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setFocusedUpgradePlan(undefined);
        }}
        currentPlan={subscriptionData}
        onUpgrade={handleUpgradeSuccess}
        focusPlanName={focusedUpgradePlan}
      />
    </div>
  );
};
